import * as React from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Badge, Drawer, Hidden, Typography, makeStyles, MenuList } from "@material-ui/core";
import {
  People as PeopleIcon,
  InsertDriveFileOutlined,
  RotateRight as RotateRightIcon,
  Settings as SettingsIcon,
  RateReview as RateReviewIcon
} from "@material-ui/icons";
import { Avatar } from "src/common/components";
import { IExtendedUser, IMyOpenTasks, UserRoles, IAppReduxState } from "src/common/types";
import { getFullName, checkUserPermissions } from "src/helpers";
import MenuItem from "./MenuItem";
import ManagerDashboardSideMenu from "./ManagerDashboardSideMenu/ManagerDashboardSideMenu";
import GradingIcon from "@mui/icons-material/Grading";
import GroupsIcon from "@mui/icons-material/Groups";
const useStyles = makeStyles((theme: any) => ({
  badgeLabel: {
    margin: "4px -4px 0 0"
  },
  badgeRoot: {
    '& [class*="icon-"]': {
      fontSize: "1.6rem"
    },
    "& svg": {
      marginRight: "0 !important"
    },
    margin: "0 18px 0 0"
  },
  contractTitle: {
    fontSize: "1.4rem",
    lineHeight: "1.9rem",
    marginBottom: 4
  },
  drawerPaper: {
    background: "none",
    border: "none",
    maxWidth: 235,
    padding: "0 0 0 20px"
  },
  fullName: {
    fontSize: "1.8rem",
    fontWeight: 600,
    lineHeight: "2.4rem",
    margin: "9px 0 3px"
  },
  menu: {
    "& a": {
      padding: "6px 6px 6px 20px"
    },
    "& svg": {
      fontSize: "22px",
      marginRight: 6
    },
    margin: "16px 0 0 -20px"
  },
  menuLabel: {
    marginRight: "7px",
    whiteSpace: "initial" as "initial"
  },
  subMenu: {
    margin: "0 0 0 45px",
    "& > a": {
      whiteSpace: "break-spaces"
    }
  },
  subMenuWithBadge: {
    margin: "0 0 0 65px",
    "& > a": {
      whiteSpace: "break-spaces"
    }
  },
  toolbar: {
    ...theme.mixins.toolbar,
    marginBottom: 30
  }
}));

interface IOwnProps {
  subPage?: string;
  currentPage: string;
  lng: string;
}

interface IMapStateToProps {
  currentUser: IExtendedUser;
  hasEmployees: boolean;
  openTasks: IMyOpenTasks;
}

type Props = IOwnProps & IMapStateToProps;

const SideMenu = ({
  currentPage,
  subPage,
  currentUser: { id: userId, photoBase64, title, department, subDepartment, firstName, lastName, middleName, userRole },
  hasEmployees,
  lng,
  openTasks: { myEmployeesCount, myFeedbacksCount, peersCount, anonymousCount }
}: Props) => {
  const fullName = getFullName([firstName, middleName, lastName]);
  const classes = useStyles();
  const { t } = useTranslation();

  const position = [department, subDepartment].filter(value => value).join(", ");

  const badgeClasses = {
    badge: classes.badgeLabel,
    root: classes.badgeRoot
  };

  return (
    <Hidden xsDown implementation="css">
      <Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" open>
        <div className={classes.toolbar} />
        <Avatar type="large" avatar={photoBase64} fullName={fullName} />
        <Typography className={classes.fullName} color="primary">
          {fullName}
        </Typography>
        <Typography className={classes.contractTitle} variant="body1">
          {position}
        </Typography>
        <Typography className={classes.contractTitle} variant="body1">
          {title}
        </Typography>
        <MenuList className={classes.menu}>
          {hasEmployees && (
            <div>
              <MenuItem
                selected={currentPage === "my-employees"}
                to={`/${lng}/dashboard/my-employees`}
                dataTestId="myEmployeesMenuItem"
              >
                <Badge
                  classes={myEmployeesCount !== 0 ? badgeClasses : {}}
                  invisible={myEmployeesCount === 0}
                  color="secondary"
                  badgeContent={myEmployeesCount}
                >
                  <GroupsIcon />
                </Badge>
                <span className={classes.menuLabel}>{t("feedback.sidemenu.my-employees")}</span>
              </MenuItem>
              <div className={myEmployeesCount !== 0 ? classes.subMenuWithBadge : classes.subMenu}>
                <MenuItem
                  selected={currentPage === "my-direct-reports-action-plans"}
                  to={`/${lng}/dashboard/my-direct-reports-action-plans`}
                  dataTestId="myDirectReportsActionPlans"
                >
                  {t("feedback.page.talk-sheet.actionPlan.myDirectReportsActionPlan")}
                </MenuItem>
              </div>
            </div>
          )}
          <MenuItem
            selected={currentPage === "my-feedbacks"}
            to={`/${lng}/dashboard/my-feedbacks`}
            dataTestId="myFeedbacksMenuItem"
          >
            <Badge
              classes={myFeedbacksCount !== 0 ? badgeClasses : {}}
              invisible={myFeedbacksCount === 0}
              color="secondary"
              badgeContent={myFeedbacksCount}
            >
              <GradingIcon />
            </Badge>
            <span className={classes.menuLabel}>{t("feedback.sidemenu.my-feedbacks")}</span>
          </MenuItem>
          <div className={myFeedbacksCount !== 0 ? classes.subMenuWithBadge : classes.subMenu}>
            <MenuItem
              selected={currentPage === "my-action-plans"}
              to={`/${lng}/dashboard/my-action-plans`}
              dataTestId="myActionPlansMenuItem"
            >
              {t("feedback.page.talk-sheet.actionPlan.myActionPlans")}
            </MenuItem>
          </div>
          <MenuItem
            selected={currentPage === "my-peers"}
            to={`/${lng}/dashboard/my-peers`}
            dataTestId="myPeersMenuItem"
          >
            <Badge
              classes={peersCount !== 0 ? badgeClasses : {}}
              invisible={peersCount === 0}
              color="secondary"
              badgeContent={peersCount}
            >
              <PeopleIcon />
            </Badge>
            <span className={classes.menuLabel}>{t("feedback.sidemenu.myPeers")}</span>
          </MenuItem>
          <MenuItem
            selected={currentPage === "anonymous-feedback"}
            to={`/${lng}/dashboard/anonymous-feedback`}
            dataTestId="anonymousFeedbackMenuItem"
          >
            <Badge
              classes={anonymousCount !== 0 ? badgeClasses : {}}
              invisible={anonymousCount === 0}
              color="secondary"
              badgeContent={anonymousCount}
            >
              <RateReviewIcon />
            </Badge>

            <span className={classes.menuLabel}>{t("feedback.sidemenu.anonymousFeedback")}</span>
          </MenuItem>
          {hasEmployees && (
            <ManagerDashboardSideMenu
              dataTestId="managerDashboardMenuItem"
              currentPage={currentPage}
              lng={lng}
              userId={userId}
              subPage={subPage}
            />
          )}
          {checkUserPermissions(userRole, [UserRoles.SuperAdmin]) ? (
            <MenuItem
              dataTestId="adminTranslatorsMenuItem"
              selected={currentPage === "admin-translators"}
              to={`/${lng}/dashboard/admin-translators`}
            >
              <SettingsIcon />
              <span className={classes.menuLabel}>{t("feedback.sidemenu.admins")}</span>
            </MenuItem>
          ) : null}
          {checkUserPermissions(userRole, [UserRoles.SuperAdmin, UserRoles.CountryAdmin]) ? (
            <MenuItem selected={currentPage === "cycles"} to={`/${lng}/dashboard/cycles`} dataTestId="cyclesMenuItem">
              <RotateRightIcon />
              {t("feedback.sidemenu.cycles")}
            </MenuItem>
          ) : null}
          {checkUserPermissions(userRole, [UserRoles.SuperAdmin, UserRoles.CountryAdmin, UserRoles.Translator]) ? (
            <div>
              <MenuItem
                selected={currentPage === "templates" && ["all", "create"].includes(subPage as string)}
                to={`/${lng}/dashboard/templates/all`}
                dataTestId="allTemplatesMenuItem"
              >
                <InsertDriveFileOutlined />
                {t("feedback.sidemenu.templates.all")}
              </MenuItem>
              <div className={classes.subMenu}>
                <MenuItem
                  selected={subPage === "supervisor"}
                  to={`/${lng}/dashboard/templates/supervisor`}
                  dataTestId="directSupervisorTemplateMenuItem"
                >
                  {t("feedback.sidemenu.templates.supervisor")}
                </MenuItem>
                <MenuItem
                  selected={subPage === "individual"}
                  to={`/${lng}/dashboard/templates/individual`}
                  dataTestId="individualTemplateMenuItem"
                >
                  {t("feedback.sidemenu.templates.individual")}
                </MenuItem>
                <MenuItem
                  selected={subPage === "peer"}
                  to={`/${lng}/dashboard/templates/peer`}
                  dataTestId="peerTemplateMenuItem"
                >
                  {t("feedback.sidemenu.templates.peer")}
                </MenuItem>
                <MenuItem
                  selected={subPage === "anonymous"}
                  to={`/${lng}/dashboard/templates/anonymousDSV`}
                  dataTestId="anonymousTemplateMenuItem"
                >
                  {t("feedback.sidemenu.templates.anonymous")}
                </MenuItem>
              </div>
            </div>
          ) : null}
        </MenuList>
      </Drawer>
    </Hidden>
  );
};

const mapStateToProps = ({ currentUser: { userData, openTasks } }: IAppReduxState): IMapStateToProps => ({
  currentUser: userData as IExtendedUser,
  hasEmployees: Boolean((userData as IExtendedUser).subordinatesCount),
  openTasks
});

export default connect(mapStateToProps)(React.memo(SideMenu));
